import React, { useState, useEffect, useRef } from 'react'
import { collection, query, orderBy, onSnapshot, where, limit } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

export default function ProductSearchInput({ onProductSelect, placeholder = "Search for a product...", value = "", onChange = null }) {
  const { userClaims } = useAuth()
  const [searchTerm, setSearchTerm] = useState(value)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef(null)
  const suggestionsRef = useRef(null)

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim() && searchTerm.length >= 2) {
        searchProducts(searchTerm.trim())
      } else {
        setSuggestions([])
        setShowSuggestions(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, userClaims?.customer])

  // Search products in master list
  const searchProducts = async (term) => {
    if (!userClaims?.customer) return

    setLoading(true)
    try {
      const collectionName = `${userClaims.customer}_products_master`
      
      // Get all products and filter client-side for case-insensitive search
      const q = query(collection(db, collectionName), orderBy('displayname'))
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const allProducts = []
        querySnapshot.forEach((doc) => {
          allProducts.push({ id: doc.id, ...doc.data() })
        })
        
        // Filter products case-insensitively; exclude disabled products
        const filteredProducts = allProducts.filter((product) => {
          if (product.enabled === false) return false
          const searchLower = term.toLowerCase()
          return (
            product.displayname?.toLowerCase().includes(searchLower) ||
            product.name?.toLowerCase().includes(searchLower) ||
            product.code?.toLowerCase().includes(searchLower) ||
            product.brand?.toLowerCase().includes(searchLower)
          )
        }).slice(0, 8) // Limit to 8 results
        
        setSuggestions(filteredProducts)
        setShowSuggestions(filteredProducts.length > 0)
        setLoading(false)
      })

      // Clean up listener after getting initial results
      setTimeout(() => unsubscribe(), 2000)
    } catch (error) {
      console.error('Error searching products:', error)
      setLoading(false)
    }
  }

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    setSelectedIndex(-1)
    
    // Call parent onChange if provided
    if (onChange) {
      onChange(value)
    }
  }

  // Handle product selection
  const handleProductSelect = (product) => {
    setSearchTerm(product.displayname)
    setShowSuggestions(false)
    setSelectedIndex(-1)
    onProductSelect(product)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleProductSelect(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text
    
    const regex = new RegExp(`(${searchTerm})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-warning text-dark">{part}</mark>
      ) : (
        part
      )
    )
  }

  return (
    <div className="position-relative">
      <div className="input-group">
        <span className="input-group-text">
          <i className="fas fa-search"></i>
        </span>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true)
            }
          }}
          autoComplete="off"
        />
        {loading && (
          <span className="input-group-text">
            <i className="fas fa-spinner fa-spin"></i>
          </span>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{ top: '100%', zIndex: 1050, maxHeight: '300px', overflowY: 'auto' }}
        >
          {suggestions.map((product, index) => (
            <div
              key={product.id}
              className={`p-3 border-bottom cursor-pointer ${
                index === selectedIndex ? 'bg-light' : ''
              }`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleProductSelect(product)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center flex-grow-1">
                  {product.imageUrl && (
                    <img 
                      src={product.imageUrl} 
                      alt={product.displayname}
                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      className="rounded border me-3"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                  <div className="flex-grow-1">
                    <div className="fw-bold">
                      {highlightText(product.displayname, searchTerm)}
                    </div>
                    <div className="small text-muted">
                      {product.name && (
                        <span className="me-2">
                          Name: {highlightText(product.name, searchTerm)}
                        </span>
                      )}
                      {product.brand && (
                        <span className="me-2">
                          Brand: {highlightText(product.brand, searchTerm)}
                        </span>
                      )}
                      {product.code && (
                        <span className="badge bg-light text-dark">
                          {highlightText(product.code, searchTerm)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <div className="fw-bold text-success">₹{product.MRP?.toFixed(2) || '0.00'}</div>
                  <div className="small text-muted">MRP</div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Manual entry option */}
          <div
            className={`p-3 text-muted cursor-pointer ${
              selectedIndex === suggestions.length ? 'bg-light' : ''
            }`}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setShowSuggestions(false)
              // Keep current search term for manual entry
            }}
            onMouseEnter={() => setSelectedIndex(suggestions.length)}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-plus-circle me-2"></i>
              <div>
                <div className="small">Use manual entry: "{searchTerm}"</div>
                <div className="small text-muted">Product not in master list</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && suggestions.length === 0 && !loading && searchTerm.length >= 2 && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{ top: '100%', zIndex: 1050 }}
        >
          <div className="p-3 text-center text-muted">
            <i className="fas fa-search me-2"></i>
            No products found. You can still use manual entry.
          </div>
        </div>
      )}
    </div>
  )
}